import React, { FC } from 'react';
import { connect } from 'react-redux';
import { Form, Modal, Radio, Spin } from 'antd';
import {
	fetchComplianceSettings,
	updateComplianceSettings,
} from '../../thunks/compliance_settings_thunks';
import { withUserType } from '../withUserType';
import { COMPLIANCE_APPROVAL_MODES, ComplianceSettings } from '../../types/ComplianceTypes';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import useMountEffect from '../../hooks/useMountEffect';

interface ComplianceSettingsModalProps {
	visible: boolean;
	// Falls back to the logged-in user's role (session.userType, injected by
	// withUserType) when not explicitly provided.
	roleType?: string;
	onCancel: () => void;
	settings: ComplianceSettings;
	fetching: boolean;
	saving: boolean;
	fetchSettings: () => Promise<any>;
	updateSettings: (settings: any) => Promise<any>;
}

const ComplianceSettingsModal: FC<ComplianceSettingsModalProps> = ({
	visible,
	onCancel,
	settings,
	fetching,
	saving,
	fetchSettings,
	updateSettings,
}): React.ReactElement => {
	const [form] = Form.useForm();

	useMountEffect(() => {
		fetchSettings().then((fetchedSettings) => {
			form.setFieldsValue({
				approvalMode: fetchedSettings.approvalMode,
				allowWorkOrdersForNonCompliant: fetchedSettings.allowWorkOrdersForNonCompliant,
			});
		});
	});

	const handleSubmit = () => {
		form.validateFields().then((values) => {
			updateSettings({ ...settings, ...values }).then(onCancel);
		});
	};

	return (
		<Modal
			visible={visible}
			title="Settings"
			okText="DONE"
			cancelText="CANCEL"
			onOk={handleSubmit}
			onCancel={onCancel}
			confirmLoading={saving}
			destroyOnClose={true}
			width={587}
		>
			{fetching ? (
				<div className="flex justify-center py-8">
					<Spin />
				</div>
			) : (
				<Form
					form={form}
					layout="vertical"
					preserve={false}
					initialValues={{
						approvalMode: nullSafeGetOrElse(
							'approvalMode',
							settings,
							COMPLIANCE_APPROVAL_MODES.NON_COMPLIANT_UNTIL_APPROVED
						),
						allowWorkOrdersForNonCompliant: nullSafeGetOrElse(
							'allowWorkOrdersForNonCompliant',
							settings,
							false
						),
					}}
				>
					<Form.Item
						name="approvalMode"
						label={
							<span className="footnoteMedium text-character-secondary">COMPLIANCE APPROVAL</span>
						}
					>
						<Radio.Group>
							<div className="flex flex-col gap-2">
								<Radio value={COMPLIANCE_APPROVAL_MODES.NON_COMPLIANT_UNTIL_APPROVED}>
									Suppliers are non-compliant until you approve
								</Radio>
								<Radio value={COMPLIANCE_APPROVAL_MODES.COMPLIANT_UNTIL_REJECTED}>
									Suppliers are compliant until you review/reject.
								</Radio>
							</div>
						</Radio.Group>
					</Form.Item>
					<Form.Item
						name="allowWorkOrdersForNonCompliant"
						label={
							<span className="footnoteMedium text-character-secondary">
								WORK ORDER DISPATCHING
							</span>
						}
					>
						<Radio.Group>
							<div className="flex flex-col gap-2">
								<Radio value={false}>
									Cannot Receive work orders if a supplier is non-compliant (unless specified)
								</Radio>
								<Radio value={true}>
									Can Receive Work orders if a supplier is non-compliant (unless specified)
								</Radio>
							</div>
						</Radio.Group>
					</Form.Item>
				</Form>
			)}
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	settings: nullSafeGetOrElse('detail', state.compliance_settings, {}),
	fetching: state.compliance_settings.fetching,
	saving: state.compliance_settings.updating,
});

const mapDispatchToProps = (dispatch, ownProps) => {
	const role = ownProps.roleType || ownProps.userType;
	return {
		fetchSettings: () => dispatch(fetchComplianceSettings(role)()),
		updateSettings: (settings) => dispatch(updateComplianceSettings(role)(settings)),
	};
};

export default withUserType(connect(mapStateToProps, mapDispatchToProps)(ComplianceSettingsModal));
